// src/components/TaskForm.jsx - ИСПРАВЛЕННАЯ ВЕРСИЯ
import React, { useState, useEffect, useRef } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  ProgressBar,
  Card,
  Badge
} from 'react-bootstrap';
import { tasksAPI } from '../services/api.js';

const TaskForm = ({ task, onSubmit, onCancel, loading, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'technical',
    due_date: '',
    estimated_hours: '',
    device_info: '',
    location: '',
    contact_info: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        category: task.category || 'technical',
        due_date: task.due_date ? new Date(task.due_date).toISOString().slice(0, 16) : '',
        estimated_hours: task.estimated_hours || '',
        device_info: task.device_info ? JSON.stringify(task.device_info, null, 2) : '',
        location: task.location || '',
        contact_info: task.contact_info || ''
      });
    }
  }, [task]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        setUploadErrors(prev => ({
          ...prev,
          [file.name]: 'Размер файла превышает 10MB'
        }));
        return false;
      }
      
      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'jpg', 'jpeg', 'png', 'gif', 'txt', 'zip', 'rar', '7z'];
      const ext = file.name.split('.').pop().toLowerCase();
      
      if (!allowedExtensions.includes(ext)) {
        setUploadErrors(prev => ({
          ...prev,
          [file.name]: 'Неподдерживаемый тип файла'
        }));
        return false;
      }
      
      return true;
    });

    const uniqueFiles = validFiles.filter(file => 
      !selectedFiles.some(existingFile => 
        existingFile.name === file.name && existingFile.size === file.size
      )
    );

    if (uniqueFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...uniqueFiles]);
    }
    
    e.target.value = '';
  };

  const handleRemoveFile = (fileName) => {
    setSelectedFiles(prev => prev.filter(file => file.name !== fileName));
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fileName];
      return newErrors;
    });
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
    setUploadSuccess(prev => {
      const newSuccess = { ...prev };
      delete newSuccess[fileName];
      return newSuccess;
    });
  };

  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    setUploadErrors({});
    setUploadProgress({});
    setUploadSuccess({});
  };

  const uploadFilesToTask = async (taskId) => {
    if (selectedFiles.length === 0) {
      console.log('📭 No files to upload');
      return { success: true, uploaded: 0, failed: 0, errors: [] };
    }

    console.log(`📤 Starting upload of ${selectedFiles.length} files to task ${taskId}`);
    setUploadingFiles(true);
    const results = {
      uploaded: 0,
      failed: 0,
      errors: []
    };

    for (const file of selectedFiles) {
      try {
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 0
        }));

        const onUploadProgress = (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(prev => ({
              ...prev,
              [file.name]: progress
            }));
          }
        };

        console.log(`📤 Uploading file: ${file.name} (${formatFileSize(file.size)})`);
        await tasksAPI.addAttachment(taskId, file, onUploadProgress);
        
        setUploadSuccess(prev => ({
          ...prev,
          [file.name]: true
        }));
        
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 100
        }));

        results.uploaded++;
        console.log(`✅ File uploaded successfully: ${file.name}`);

      } catch (err) {
        console.error(`❌ Error uploading file ${file.name}:`, err);
        const errorMsg = err.response?.data?.detail || err.message || 'Ошибка загрузки';
        setUploadErrors(prev => ({
          ...prev,
          [file.name]: errorMsg
        }));
        results.failed++;
        results.errors.push({ file: file.name, error: errorMsg });
      }
    }

    setUploadingFiles(false);
    console.log('📊 Final upload results:', results);
    return results;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Название задачи обязательно';
    }
    
    if (formData.title.length > 255) {
      newErrors.title = 'Название не должно превышать 255 символов';
    }
    
    if (formData.estimated_hours && (formData.estimated_hours < 1 || formData.estimated_hours > 1000)) {
      newErrors.estimated_hours = 'Оценка времени должна быть от 1 до 1000 часов';
    }
    
    if (formData.device_info) {
      try {
        JSON.parse(formData.device_info);
      } catch (err) {
        newErrors.device_info = 'Неверный формат JSON';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setUploadErrors({});
    setUploadSuccess({});
    
    try {
      const submitData = {
        ...formData,
        estimated_hours: formData.estimated_hours ? parseInt(formData.estimated_hours) : null,
        device_info: formData.device_info ? JSON.parse(formData.device_info) : null,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null
      };
      
      console.log('📝 Submitting task data:', submitData);
      console.log('📎 Selected files:', selectedFiles.length);
      
      const result = await onSubmit(submitData);
      console.log('✅ Task creation result:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Не удалось создать задачу');
      }
      
      // ИСПРАВЛЕНИЕ: проверяем наличие taskId в разных форматах
      const taskId = result.taskId || result.id || result.task?.id;
      
      if (!taskId) {
        console.error('❌ No task ID in response:', result);
        throw new Error('Не получен ID созданной задачи');
      }
      
      if (selectedFiles.length > 0) {
        console.log('📤 Starting file upload for task:', taskId);
        
        const uploadResults = await uploadFilesToTask(taskId);
        console.log('📊 Upload results:', uploadResults);
        
        if (uploadResults.failed > 0) {
          const errorMessages = uploadResults.errors.map(e => `${e.file}: ${e.error}`).join('; ');
          alert(`Не удалось загрузить ${uploadResults.failed} из ${selectedFiles.length} файлов: ${errorMessages}`);
        }
        
        if (result.task && uploadResults.uploaded > 0) {
          try {
            const freshTask = await tasksAPI.getTask(taskId);
            result.task = freshTask;
          } catch (err) {
            console.error('❌ Error reloading task:', err);
          }
        }
      }
      
      if (onSuccess) {
        onSuccess(result);
      }
      
    } catch (err) {
      console.error('❌ Form submission error:', err);
      alert(`Ошибка при создании задачи: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const icons = {
      pdf: 'bi-file-pdf',
      doc: 'bi-file-word',
      docx: 'bi-file-word',
      xls: 'bi-file-excel',
      xlsx: 'bi-file-excel',
      ppt: 'bi-file-ppt',
      pptx: 'bi-file-ppt',
      jpg: 'bi-file-image',
      jpeg: 'bi-file-image',
      png: 'bi-file-image',
      gif: 'bi-file-image',
      txt: 'bi-file-text',
      zip: 'bi-file-zip',
      rar: 'bi-file-zip',
      '7z': 'bi-file-zip'
    };
    return icons[ext] || 'bi-file-earmark';
  };

  const getFileIconColor = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const colors = {
      pdf: 'text-danger',
      doc: 'text-primary',
      docx: 'text-primary',
      xls: 'text-success',
      xlsx: 'text-success',
      ppt: 'text-warning',
      pptx: 'text-warning',
      jpg: 'text-info',
      jpeg: 'text-info',
      png: 'text-info',
      gif: 'text-info',
      txt: 'text-secondary',
      zip: 'text-dark',
      rar: 'text-dark',
      '7z': 'text-dark'
    };
    return colors[ext] || 'text-primary';
  };

  const priorities = [
    { value: 'low', label: 'Низкий', color: 'success' },
    { value: 'medium', label: 'Средний', color: 'info' },
    { value: 'high', label: 'Высокий', color: 'warning' },
    { value: 'urgent', label: 'Срочный', color: 'danger' }
  ];

  const categories = [
    { value: 'technical', label: 'Техническая проблема' },
    { value: 'administrative', label: 'Административный вопрос' },
    { value: 'feature_request', label: 'Запрос функционала' },
    { value: 'bug', label: 'Ошибка' },
    { value: 'bil', label: 'БИЛ' },
    { value: 'invoices_registers', label: 'Счета Реестры' },
    { value: 'filling', label: 'Набивалка' },
    { value: 'other', label: 'Другое' }
  ];

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="g-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Название задачи *</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Введите название задачи"
              isInvalid={!!errors.title}
              maxLength={255}
            />
            <Form.Control.Feedback type="invalid">
              {errors.title}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group>
            <Form.Label>Описание</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Опишите детали задачи..."
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Приоритет</Form.Label>
            <Form.Select
              name="priority"
              value={formData.priority}
              onChange={handleChange}
            >
              {priorities.map(priority => (
                <option key={priority.value} value={priority.value}>
                  {priority.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Категория</Form.Label>
            <Form.Select
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Срок выполнения</Form.Label>
            <Form.Control
              type="datetime-local"
              name="due_date"
              value={formData.due_date}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Оценка времени (часы)</Form.Label>
            <Form.Control
              type="number"
              name="estimated_hours"
              value={formData.estimated_hours}
              onChange={handleChange}
              placeholder="Оцените время выполнения"
              min="1"
              max="1000"
              isInvalid={!!errors.estimated_hours}
            />
            <Form.Control.Feedback type="invalid">
              {errors.estimated_hours}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Местоположение</Form.Label>
            <Form.Control
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Где нужно выполнить задачу?"
              maxLength={255}
            />
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group>
            <Form.Label>Контактная информация</Form.Label>
            <Form.Control
              type="text"
              name="contact_info"
              value={formData.contact_info}
              onChange={handleChange}
              placeholder="Телефон или email для связи"
              maxLength={255}
            />
          </Form.Group>
        </Col>
{/* 
        <Col md={12}>
          <Form.Group>
            <Form.Label>Информация об устройстве (JSON)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="device_info"
              value={formData.device_info}
              onChange={handleChange}
              placeholder='{"os": "Windows 10", "browser": "Chrome", ...}'
              isInvalid={!!errors.device_info}
            />
            <Form.Text className="text-muted">
              Дополнительная техническая информация в формате JSON
            </Form.Text>
            <Form.Control.Feedback type="invalid">
              {errors.device_info}
            </Form.Control.Feedback>
          </Form.Group>
        </Col> */}

        {!task && (
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Card.Title className="mb-0">
                    <i className="bi bi-paperclip me-2"></i>
                    Прикрепленные файлы
                    {selectedFiles.length > 0 && (
                      <Badge bg="primary" className="ms-2">
                        {selectedFiles.length}
                      </Badge>
                    )}
                  </Card.Title>
                  {selectedFiles.length > 0 && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleClearAllFiles}
                      disabled={submitting || uploadingFiles}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Очистить все
                    </Button>
                  )}
                </div>
                
                <div className="mb-3">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    style={{ display: 'none' }}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar,.7z"
                    disabled={submitting || uploadingFiles}
                  />
                  <Button
                    variant="outline-primary"
                    onClick={() => fileInputRef.current.click()}
                    disabled={submitting || uploadingFiles}
                    className="mb-2"
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    Добавить файлы
                  </Button>
                  <Form.Text className="text-muted d-block">
                    Поддерживаемые форматы: PDF, DOC/DOCX, XLS/XLSX, PPT/PPTX, JPG/PNG/GIF, TXT, ZIP/RAR/7Z (максимум 10MB каждый)
                  </Form.Text>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mt-3">
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Файл</th>
                            <th style={{ width: '100px' }}>Размер</th>
                            <th style={{ width: '120px' }}>Тип</th>
                            <th style={{ width: '100px' }}>Статус</th>
                            <th style={{ width: '60px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedFiles.map((file, index) => (
                            <tr key={index} className={uploadErrors[file.name] ? 'table-danger' : uploadSuccess[file.name] ? 'table-success' : ''}>
                              <td className="align-middle">
                                <i className={`bi ${getFileIcon(file.name)} ${getFileIconColor(file.name)} fs-5`}></i>
                              </td>
                              <td className="align-middle">
                                <div className="d-flex flex-column">
                                  <span className="fw-semibold text-truncate" style={{ maxWidth: '250px' }}>
                                    {file.name}
                                  </span>
                                  {uploadErrors[file.name] && (
                                    <small className="text-danger">
                                      <i className="bi bi-exclamation-triangle me-1"></i>
                                      {uploadErrors[file.name]}
                                    </small>
                                  )}
                                  {uploadSuccess[file.name] && (
                                    <small className="text-success">
                                      <i className="bi bi-check-circle me-1"></i>
                                      Загружено
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td className="align-middle">
                                <span className="text-muted">{formatFileSize(file.size)}</span>
                              </td>
                              <td className="align-middle">
                                <Badge bg="light" text="dark" className="border">
                                  {file.type ? file.type.split('/')[1].toUpperCase() : file.name.split('.').pop().toUpperCase()}
                                </Badge>
                              </td>
                              <td className="align-middle">
                                {uploadProgress[file.name] !== undefined ? (
                                  <div>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <small className="text-muted">{uploadProgress[file.name]}%</small>
                                    </div>
                                    <ProgressBar 
                                      now={uploadProgress[file.name]} 
                                      animated 
                                      striped 
                                      variant="primary"
                                      style={{ height: '4px' }}
                                    />
                                  </div>
                                ) : uploadingFiles ? (
                                  <Badge bg="warning">В очереди</Badge>
                                ) : (
                                  <Badge bg="secondary">Будет загружен</Badge>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveFile(file.name)}
                                  disabled={submitting || uploadingFiles || uploadProgress[file.name] !== undefined}
                                  className="border-0"
                                  title="Удалить файл"
                                >
                                  <i className="bi bi-x-lg"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="mt-3 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-muted small">
                          Всего файлов: <strong>{selectedFiles.length}</strong> • 
                          Общий размер: <strong>
                            {formatFileSize(selectedFiles.reduce((total, file) => total + file.size, 0))}
                          </strong>
                        </div>
                        {uploadingFiles && (
                          <div className="small">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span className="text-warning">Загрузка файлов...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        <Col xs={12}>
          <div className="d-flex gap-2 justify-content-end border-top pt-3">
            <Button
              variant="secondary"
              onClick={onCancel}
              disabled={submitting || uploadingFiles}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting || uploadingFiles}
            >
              {submitting ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  {task ? 'Сохранение...' : 'Создание задачи...'}
                </>
              ) : uploadingFiles ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Загрузка файлов...
                </>
              ) : (
                <>
                  <i className={`bi ${task ? 'bi-check-lg' : 'bi-plus-lg'} me-2`}></i>
                  {task ? 'Сохранить изменения' : 'Создать задачу'}
                </>
              )}
            </Button>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default TaskForm;